import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useMemo,
  useEffect,
} from "react";

export interface FilterTypeProps {
  openVIIRS: boolean;
  openNPP: boolean;
  openNORR: boolean;
  openModis: boolean;
  openALRO: boolean;
  openAgriculture: boolean;
  openProtectedForest: boolean;
  openConservedForest: boolean;
  openHighway: boolean;
  openVillage: boolean;
  openOtherCountry: boolean;
  openProvice: boolean;
  openDistrict: boolean;
  openSubDistrict: boolean;
}

interface FilterMapContextType {
  filterMap: FilterTypeProps;
  baseMapUrl: string;
  onSelectFilter: (keyword: string, status: boolean) => void;
  onSelectBaseMap: (urlMap: string) => void;
}

interface FilterMapProviderProps {
  children: ReactNode;
}

const FilterMapContext = createContext<FilterMapContextType | undefined>(
  undefined
);

export function useFilterMap() {
  const context = useContext(FilterMapContext);
  if (!context) {
    throw new Error("useFilterMap must be used within a FilterMapProvider");
  }
  return context;
}

export function FilterMapProvider({ children }: FilterMapProviderProps) {
  // const accessToken = localStorage.getItem("access_token");
  const [filterMap, setFilterMap] = useState<FilterTypeProps>({
    openVIIRS: true,
    openNPP: true,
    openNORR: true,
    openModis: false,
    openALRO: true,
    openAgriculture: true,
    openProtectedForest: true,
    openConservedForest: true,
    openHighway: true,
    openVillage: true,
    openOtherCountry: true,
    openProvice: false,
    openDistrict: false,
    openSubDistrict: false,
  });
  const [baseMapUrl, setBaseMapUrl] = useState<string>("topography");

  const onSelectFilter = useCallback((keyword: string, status: boolean) => {
    if (filterMap.hasOwnProperty(keyword)) {
      if (keyword === "openVIIRS") {
        if (status) {
          setFilterMap((prevFilterItems) => ({
            ...prevFilterItems,
            [keyword]: status,
            openNORR: true,
            openNPP: true,
          }));
        } else {
          setFilterMap((prevFilterItems) => ({
            ...prevFilterItems,
            [keyword]: status,
            openNORR: false,
            openNPP: false,
          }));
        }
      } else {
        setFilterMap((prevFilterItems) => ({
          ...prevFilterItems,
          [keyword]: status,
        }));
      }
    } else {
      console.error(`Keyword "${keyword}" does not exist in filterItems.`);
    }
  }, []);

  const onSelectBaseMap = useCallback((mapStyle: string) => {
    if (mapStyle === "satellite") {
      setBaseMapUrl("satellite");
    } else if (mapStyle === "topography") {
      setBaseMapUrl("topography");
    } else {
      setBaseMapUrl("hybrid");
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      filterMap,
      onSelectFilter,
      baseMapUrl,
      onSelectBaseMap,
    }),
    [filterMap, onSelectFilter, baseMapUrl, onSelectBaseMap]
  );

  useEffect(() => {
    let item = filterMap;
    if (!item.openNORR && !item.openNPP) {
      item.openVIIRS = false;
      setFilterMap(item);
    }
    if (item.openNORR || item.openNPP) {
      item.openVIIRS = true;
      setFilterMap(item);
    }
  }, [filterMap]);

  return (
    <FilterMapContext.Provider value={contextValue}>
      {children}
    </FilterMapContext.Provider>
  );
}
